import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";
import { AuthActionCreators, IApplicationState } from "../";
import { ICenterCoordinator } from "@nkr/models/center-coordinator";
import { ICenterRecipient } from "@nkr/models/center-recipient";
import { IStatusList, ISwapDonor, ITypeList, ISwapDonorCluster, IConvertDonationType } from '@nkr/models/swap-donors';
import { IBaseResponse, ICenterBase, IDonor, IListItem, IRecipient } from '@nkr/models/index';
import { DonationType, VoucherStatusType } from '@nkr/enums/index';

export const ActionTypes = {
    SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
    UPDATE_SEARCH_RESULTS: 'UPDATE_SEARCH_RESULTS'
}

export interface ISwapDonorLookupData {
    donorChainId: number,
    position: number,
    clusterNumber: number,
    recipientList: ICenterRecipient[],
    centerList: IListItem[],
    remoteCenterList: IListItem[],
    clusterList: IListItem[],
    positionList: IListItem[],
    chainList: IListItem[],
    statusList: IListItem[],
    donationList: IListItem[],
    expertSystemStatusList: IListItem[],
}

export interface ISwapDonorLookUpDataResponse {
    data: {
        success: boolean,
        message: string,
        results: ISwapDonorLookupData
    }
}

export interface ISwapDonorDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            donorDonorDataProfile: ISwapDonor
        }
    }
}

export interface IUpdateDonorDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            donorDonorDataProfile: ISwapDonor
        }
    }
}

export interface ISwapProfileSearchResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            list: ISwapDonor[],
            total: number
        }
    }
}

export interface ISwapDonorsLookupDataResponse {
    data: {
        success: boolean,
        results: {
            coordinatorList: ICenterCoordinator[],
            centerList: IListItem[],
            donationTypeList: IListItem[],
            rdnCenterList: ICenterBase[]
        }
    }
}

export interface ISwapDonorResponse {
    data: {
        success: boolean,
        results: ISwapDonor;
    }
}

export interface ISwapDonorDeleteResponse {
    data: {
        success: boolean
    }
}

export interface ISpecificSwapProfileSearchResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            donor: ISwapDonor,
            total: number
        }
    }
}
export interface IPairingRecipientDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipient[]
    }
}
export interface IDonorAliasDuplicateCheckResponse {
    data: {
        success: boolean
    }
}

export interface IDonorConversionDataLookupResponse {
    data: {
        success: boolean,
        results: {
            isPaired: boolean,
            hasIntended: boolean,
            isRemote: boolean,
            isExpertSystem: boolean,
            hasDashReferral: boolean,
            typeList: IListItem[],
        }
    }
}

export interface IDonorConversionPostResponse {
    data: {
        success: boolean,
        message: string
    }
}

export interface IDonorSearchRequest {
    centerId: number,
    fullName: string,
    alias: string,
    email: string,
    remote: number,
    dob: string,
    statuses: IStatusList,
    types: ITypeList,
    donatedStartDate: string,
    donatedEndDate: string,
    compatible: boolean,
    voucherStatus: VoucherStatusType
}

export const ActionCreators = {
    setSearchResults: (results: ISwapDonor[]) => {
        return {
            type: ActionTypes.SET_SEARCH_RESULTS,
            results
        };
    },
    updateSearchResults: (identifier: string, results: ISwapDonor[]) => {
        return {
            type: ActionTypes.UPDATE_SEARCH_RESULTS,
            identifier,
            results
        };
    },
    doAdminDonorSearch: (request: IDonorSearchRequest) => (dispatch: Dispatch<IApplicationState>): Promise<ISwapProfileSearchResponse> => {
        let { centerId, fullName, alias, email, remote, dob, statuses, types, compatible, voucherStatus } = request;

        let options = {
            url: `${__API__}/admin/donor/search`,
            data: {
                centerId,
                fullName,
                alias,
                email,
                remote,
                dob,
                statuses,
                types,
                donatedStartDate: '',
                donatedEndDate: '',
                compatible,
                voucherStatus
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: ISwapProfileSearchResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    doCenterDonorSearch: (request: IDonorSearchRequest) => (dispatch: Dispatch<IApplicationState>): Promise<ISwapProfileSearchResponse> => {
        let { centerId, fullName, alias, email, remote, dob, statuses, types, donatedStartDate, donatedEndDate, compatible, voucherStatus } = request;

        let options = {
            url: `${__API__}/center/${request.centerId}/swap-donors`,
            data: {
                centerId,
                fullName,
                alias,
                email,
                remote,
                dob,
                statuses,
                types,
                donatedStartDate,
                donatedEndDate,
                compatible,
                voucherStatus
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: ISwapProfileSearchResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    doAdminSave: (donor: ISwapDonor) => (dispatch: Dispatch<IApplicationState>): Promise<ISwapDonorResponse> => {
        let { mrn, alias, centerId, statusId, isDeleted, sendComplianceEmails, isInDonorFollowUpProgram,
            donorId, fullName, email, isActive, birthDate, gender, donationStartDate, donationEndDate,
            donationTypeId, donorChainId, clusterNumber, recipientId: pairedRecipientId, position, rdnCenterId, expertSystemStatusId, isRealTimeSwapFailure,
            isAddBackCredit, addBackCreditChangeReason } = donor;

        if (donor.donationTypeId == DonationType.StandardVoucher || donor.donationTypeId == DonationType.FamilyVoucher) {
            pairedRecipientId = donor.adpRecipientId;
        }

        let data = {
            mrn, alias, centerId, statusId, isDeleted, sendComplianceEmails, isInDonorFollowUpProgram,
            donorId, fullName, email, isActive, birthDate, gender, donationStartDate, donationEndDate,
            donationTypeId, donorChainId, clusterNumber, position, pairedRecipientId, rdnCenterId, expertSystemStatusId,
            isRealTimeSwapFailure, isAddBackCredit, addBackCreditChangeReason
        };

        var options = {
            url: `${__API__}/admin/donor/${donor.donorId}/update-donor`,
            data
        };

        return dispatch(AuthActionCreators.apiPut(options))
            .then((response: ISwapDonorResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    doCenterSave: (donor: ISwapDonor) => (dispatch: Dispatch<IApplicationState>): Promise<ISwapDonorResponse> => {
        let { donorId, fullName, email, isActive, birthDate, gender, donationStartDate, donationEndDate, rdnCenterId } = donor;

        let data = {
            donorId, fullName, email, isActive, birthDate, gender, donationStartDate, donationEndDate, rdnCenterId
        };

        var options = {
            url: `${__API__}/center/${donor.centerId}/donor/${donor.donorId}/update-donor`,
            data
        };

        return dispatch(AuthActionCreators.apiPut(options))
            .then((response: ISwapDonorResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    getDonorData: (donorId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ISwapDonorDataResponse> => {
        let options = {
            url: `${__API__}/donor/${donorId}/profile/${centerId}`,
            data: {
                centerId
            }
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getLookUpData: (donorId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ISwapDonorLookUpDataResponse> => {
        let options = {
            url: `${__API__}/donor/${centerId}/donor/${donorId}/lookup-data`,
            data: {
                centerId,
                donorId
            }
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getSwapDonorsLookupData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ISwapDonorsLookupDataResponse> => {
        let options = {
            url: `${__API__}/donor/profile/lookup-data-by-center/${centerId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    doAdminDelete: (donorId: number, reasonCode: number, reason: string, initials: string) => async (dispatch: Dispatch<IApplicationState>): Promise<ISwapDonorDeleteResponse> => {
        let options = {
            url: `${__API__}/admin/donor/${donorId}/delete-donor`,
            data: {
                reasonCode,
                reason,
                initials
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    doCenterDelete: (donorId: number, centerId: number, reasonCode: number, reason: string, initials: string) => async (dispatch: Dispatch<IApplicationState>): Promise<ISwapDonorDeleteResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/donor/${donorId}/delete-donor`,
            data: {
                reasonCode,
                reason,
                initials
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    doSpecificAdminDonorSearch: (donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ISpecificSwapProfileSearchResponse> => {
        let options = {
            url: `${__API__}/admin/donor/${donorId}/search-specific-donor`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    checkAlias: (donorId: number, alias: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorAliasDuplicateCheckResponse> => {
        let options = {
            url: `${__API__}/donor/${donorId}/check-alias-availability`,
            data: {
                alias
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    sendSelfRegistrationInvite: (donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/donor/${donorId}/profile/initiate-self-registration`
        }

        return await dispatch(AuthActionCreators.apiGet(options))
    },
    getPairingRecipientList: () => (dispatch: Dispatch<IApplicationState>): Promise<IPairingRecipientDataResponse> => {
        var options = {
            url: `${__API__}/donor/paired-recipient-list/`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IPairingRecipientDataResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    savePairingRecipient: (centerId: number, donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IPairingRecipientDataResponse> => {
        var options = {
            url: `${__API__}/admin/donor/convert-to-paired/`,
            data: {
                donorId,
                recipientId
            }
        };

        return dispatch(AuthActionCreators.apiPut(options))
            .then((response: IPairingRecipientDataResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    getConversionLookUpData: (donorId: number, centerId: number, isAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorConversionDataLookupResponse> => {
        let url = isAdmin ? `${__API__}/admin/donor/${donorId}/donation-type-lookup` : `${__API__}/center/${centerId}/donor/${donorId}/donation-type-lookup`;

        let options = { url };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    convertDonor: (donorId: number, centerId: number, data: IConvertDonationType, isAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorConversionPostResponse> => {
        let url = isAdmin ? `${__API__}/admin/donor/${donorId}/donation-type-convert` : `${__API__}/center/${centerId}/donor/${donorId}/donation-type-convert`;

        let options = {
            url,
            data
        }

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    unlinkRecipient: (donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let url = `${__API__}/admin/donor/${donorId}/unlink-recipient`;

        let options = {
            url
        }

        return await dispatch(AuthActionCreators.apiPut(options));
    }
}

export interface ISwapSearchState {
    searchResults: ISwapDonor[]
}

var initialState: ISwapSearchState = {
    searchResults: []
};

interface ISetSearchResults {
    type: string,
    results: ISwapDonor[]
}

interface IUpdateSearchResults {
    type: string,
    identifier: string,
    results: ISwapDonor
}

const setSearchResults = (state: ISwapSearchState, action: ISetSearchResults) => {
    state.searchResults = action.results;
    return state;
}

interface ISwapSearchHandlers {
    [x: string]:
    ((state: ISwapSearchState, action: ISetSearchResults) => ISwapSearchState)
    | ((state: ISwapSearchState, action: IUpdateSearchResults) => ISwapSearchState)
}

const createReducer = (initialState: ISwapSearchState, handlers: ISwapSearchHandlers) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
    [ActionTypes.SET_SEARCH_RESULTS]: setSearchResults//,
    // [ActionTypes.UPDATE_SEARCH_RESULTS]: updateSearchResults
};

export const Reducer = createReducer(initialState, handlers);